import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import EmailEditor from 'react-email-editor';
import { theme } from '../theme';
import { useIsMobile } from '../hooks/useMediaQuery';

function PdfTemplateEditor({ token }) {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { id } = useParams();
  const editorRef = useRef(null);
  const [nombre, setNombre] = useState('');
  const [savedDesign, setSavedDesign] = useState(null);
  const [editorReady, setEditorReady] = useState(false);
  const [loading, setLoading] = useState(!!id);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!id) { setLoading(false); return; }
    fetch(`/api/pdf-templates/${id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) {
          setNombre(data.nombre);
          const design = typeof data.design === 'string' ? JSON.parse(data.design) : data.design;
          setSavedDesign(design);
        }
      })
      .finally(() => setLoading(false));
  }, [id, token]);

  useEffect(() => {
    if (editorReady && savedDesign && editorRef.current) {
      editorRef.current.loadDesign(savedDesign);
    }
  }, [editorReady, savedDesign]);

  const onReady = () => setEditorReady(true);

  const onImageUpload = async (file, done) => {
    const formData = new FormData();
    formData.append('file', file.attachments[0]);
    try {
      const res = await fetch('/api/templates/upload-media', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      done({ progress: 100, url: `${window.location.origin}${data.url}` });
    } catch {
      done({ progress: 0 });
    }
  };

  const handleSave = () => {
    if (!nombre.trim()) { setError('El nombre es requerido'); return; }
    if (!editorRef.current) return;
    setSaving(true);
    setError('');
    editorRef.current.exportHtml(async ({ html, design }) => {
      try {
        const url = id ? `/api/pdf-templates/${id}` : '/api/pdf-templates';
        const method = id ? 'PUT' : 'POST';
        const res = await fetch(url, {
          method,
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ nombre: nombre.trim(), design, html }),
        });
        const data = await res.json();
        if (!res.ok) { setError(data.message || 'Error al guardar'); setSaving(false); return; }
        navigate('/plantillas-pdf', { state: { saved: true } });
      } catch {
        setError('Error de conexión');
        setSaving(false);
      }
    });
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 'calc(100vh - 64px)' }}>
        <p style={{ color: theme.colors.textMuted }}>Cargando...</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', backgroundColor: theme.colors.background }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? '0.6rem' : '1rem', flexWrap: isMobile ? 'wrap' : 'nowrap', padding: isMobile ? '0.6rem 1rem' : '0.6rem 1.5rem', backgroundColor: theme.colors.surface, borderBottom: `1px solid ${theme.colors.border}`, flexShrink: 0 }}>
        <button
          onClick={() => navigate('/plantillas-pdf')}
          style={{ background: 'none', border: 'none', color: theme.colors.textMuted, cursor: 'pointer', fontSize: '0.9rem', padding: 0, flexShrink: 0 }}
        >
          ← Volver
        </button>
        <input
          type="text"
          value={nombre}
          onChange={e => setNombre(e.target.value)}
          placeholder="Nombre de la plantilla PDF"
          style={{ flex: 1, minWidth: isMobile ? '160px' : 0, padding: '7px 12px', backgroundColor: theme.colors.background, border: `1px solid ${theme.colors.border}`, borderRadius: '7px', color: theme.colors.text, fontSize: '0.95rem', outline: 'none', boxSizing: 'border-box' }}
        />
        {error && <span style={{ color: theme.colors.error, fontSize: '0.85rem', flexShrink: 0 }}>{error}</span>}
        <button
          onClick={handleSave}
          disabled={saving}
          style={{ padding: '8px 24px', backgroundColor: saving ? theme.colors.border : theme.colors.primary, color: theme.colors.background, border: 'none', borderRadius: '7px', fontSize: '0.95rem', fontWeight: '700', cursor: saving ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
        >
          {saving ? 'Guardando...' : 'Guardar'}
        </button>
      </div>
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        <EmailEditor
          ref={editorRef}
          onReady={onReady}
          onImageUpload={onImageUpload}
          minHeight="100%"
          options={{ locale: 'es-ES', features: { imageEditor: false } }}
          style={{ height: '100%' }}
        />
      </div>
    </div>
  );
}

export default PdfTemplateEditor;
